import { FC } from "react";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  MultiselectBoundTemplate,
  NumberTemplate,
  RadioGroupTemplate,
  TemplateWrapper,
} from "@ryde/controls";
import {
  CopyToSupplierExistingBehavior,
  ICopyToSupplierPriceRatesProps,
  IInjectedDrawerProps,
  PriceRateAdjustmentType,
  PriceRoundingBehavior,
} from "@ryde/shared";

import { CommonDefaultDrawer } from "../common-default-drawer";
import {
  BaseDrawer,
  DrawerFormWrapperStyled,
  DrawerHeader,
  FormFooter,
} from "../drawer";
import { CopyToSupplierPriceRatesForm } from "./copy-to-supplier-price-rates-drawer.models";
import { useCopyToSupplierPriceRatesDrawer } from "./use-copy-to-supplier-price-rates-drawer";

export const CopyToSupplierPriceRatesDrawer: FC<
  ICopyToSupplierPriceRatesProps & IInjectedDrawerProps
> = ({ onCloseDrawer }) => {
  const { t } = useTranslation();
  const { methods, isSaveDisabled, onSaveClick } =
    useCopyToSupplierPriceRatesDrawer();

  const { watch } = methods ?? {};

  const adjustmentType = watch(CopyToSupplierPriceRatesForm.adjustmentType);

  const isPercentage = adjustmentType === PriceRateAdjustmentType.Percentage;

  return (
    <BaseDrawer onCloseDrawer={onCloseDrawer}>
      <CommonDefaultDrawer
        className="drawer-form"
        onCloseDrawer={onCloseDrawer}
        Header={
          <DrawerHeader
            title={t("priceRate.copyToSupplierPriceRates")}
            onCloseDrawer={onCloseDrawer}
          />
        }
        Footer={
          <FormFooter
            cancelLabel={t("general.CLOSE")}
            onCancelClick={onCloseDrawer}
            onSaveClick={onSaveClick}
            isSaveDisabled={isSaveDisabled}
          />
        }
      >
        <FormProvider {...methods}>
          <DrawerFormWrapperStyled>
            <TemplateWrapper>
              <RadioGroupTemplate
                name={CopyToSupplierPriceRatesForm.adjustmentType}
                formLabel={t("priceRate.adjustmentType")}
                formAttributes={{
                  radioGroupItems: [
                    {
                      value: PriceRateAdjustmentType.Percentage,
                      label: t("priceRate.percentage"),
                    },
                    {
                      value: PriceRateAdjustmentType.Amount,
                      label: t("priceRate.amount"),
                    },
                  ],
                }}
              />
            </TemplateWrapper>
            <TemplateWrapper>
              <NumberTemplate
                name={CopyToSupplierPriceRatesForm.adjustmentValue}
                formLabel={t("priceRate.adjustmentValue")}
              />
            </TemplateWrapper>
            {isPercentage && (
              <>
                <TemplateWrapper>
                  <RadioGroupTemplate
                    name={CopyToSupplierPriceRatesForm.roundingBehavior}
                    formLabel={t("priceRate.roundingBehavior")}
                    formAttributes={{
                      radioGroupItems: [
                        {
                          value: PriceRoundingBehavior.Floor,
                          label: t("priceRate.roundingFloor"),
                        },
                        {
                          value: PriceRoundingBehavior.Ceil,
                          label: t("priceRate.roundingCeil"),
                        },
                      ],
                    }}
                  />
                </TemplateWrapper>
                <TemplateWrapper>
                  <NumberTemplate
                    name={CopyToSupplierPriceRatesForm.decimalDigits}
                    formLabel={t("priceRate.decimalDigits")}
                  />
                </TemplateWrapper>
              </>
            )}
            <TemplateWrapper>
              <MultiselectBoundTemplate
                name={CopyToSupplierPriceRatesForm.supplierIds}
                formLabel={t("supplier.plural")}
                formAttributes={{
                  dropItemsSource: "supplier",
                }}
              />
            </TemplateWrapper>
            <TemplateWrapper>
              <RadioGroupTemplate
                name={CopyToSupplierPriceRatesForm.existingBehavior}
                formLabel={t("priceRate.existingBehavior")}
                formAttributes={{
                  radioGroupItems: [
                    {
                      value: CopyToSupplierExistingBehavior.Skip,
                      label: t("priceRate.skipExisting"),
                    },
                    {
                      value: CopyToSupplierExistingBehavior.Overwrite,
                      label: t("priceRate.overwriteExisting"),
                    },
                  ],
                }}
              />
            </TemplateWrapper>
          </DrawerFormWrapperStyled>
        </FormProvider>
      </CommonDefaultDrawer>
    </BaseDrawer>
  );
};
